import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { requestsApi, Request, startPointsApi, StartPoint, Patient } from '../../services/api';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useScrollToError } from '../../hooks/useScrollToError';
import { Paperclip, X, ArrowLeft, Save } from 'lucide-react';
import './EditRequest.css';

function EditRequest() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [request, setRequest] = useState<Request | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'PERSONNEL' as 'PERSONNEL' | 'MATERIAL' | 'MEDEVAC' | 'TRANSPORT' | 'OTHER' | 'NEWS',
    urgency: 'NORMAL' as 'HIGH' | 'NORMAL',
    answerBy: '',
    exitPointId: '',
    readyAtHub: '',
    medevacRemarks: ''
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useScrollToError(error);
  const [availableStartPoints, setAvailableStartPoints] = useState<StartPoint[]>([]);

  const loadRequest = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await requestsApi.getById(id);
      const req = response.data;

      // Check if user can edit this request
      if (req.status !== 'DRAFT') {
        setError('Only draft requests can be edited');
        setLoading(false);
        return;
      }

      if (req.creatorId !== user?.id) {
        setError('You can only edit your own requests');
        setLoading(false);
        return;
      }

      setRequest(req);
      setFormData({
        title: req.title,
        description: req.description || '',
        category: req.category,
        urgency: req.urgency,
        answerBy: req.answerBy ? new Date(req.answerBy).toISOString().slice(0, 16) : '',
        exitPointId: req.exitPoint?.id || '',
        readyAtHub: req.readyAtHub ? new Date(req.readyAtHub).toISOString().slice(0, 16) : '',
        medevacRemarks: req.medevacRemarks || ''
      });
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
    loadAvailableStartPoints();
  }, [id]);

  const loadAvailableStartPoints = async () => {
    try {
      if (user?.nation) {
        const response = await startPointsApi.getAll({ nation: user.nation });
        setAvailableStartPoints(response.data);
      }
    } catch (err) {
      console.error('Failed to load available hubs:', err);
    }
  };

  // Only allow nations, EU, EXCON, and ADMIN to edit requests
  if (!['NATION', 'EU', 'EXCON', 'ADMIN'].includes(user?.role || '')) {
    return (
      <div className="error-page">
        <h2>{t('common.error')}</h2>
        <p>{t('common.pageNotFound')}</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          {t('common.backToDashboard')}
        </button>
      </div>
    );
  }

  const canCreateNews = ['EU', 'EXCON', 'ADMIN'].includes(user?.role || '');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Validate total number of files
    if (attachments.length + files.length > 5) {
      setError('Maximum 5 files allowed');
      return;
    }

    // Validate each file
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/pdf',
      'application/zip',
      'application/x-zip-compressed'
    ];

    const validExtensions = /\.(xlsx?|docx?|pdf|zip)$/i;

    for (const file of files) {
      // Validate file type
      if (!validTypes.includes(file.type) && !validExtensions.test(file.name)) {
        setError(`File ${file.name}: Only Excel (.xls, .xlsx), Word (.doc, .docx), PDF (.pdf), and ZIP (.zip) files are allowed`);
        return;
      }

      // Validate file size (5 MB per file)
      if (file.size > 5 * 1024 * 1024) {
        setError(`File ${file.name}: Maximum file size is 5 MB`);
        return;
      }
    }

    setAttachments(prev => [...prev, ...files]);
    setError('');

    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent, submitForReview: boolean = false) => {
    e.preventDefault();
    if (!id) return;

    setError('');
    setSaving(true);

    try {
      // Update request
      const updateData: any = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        urgency: formData.urgency,
        answerBy: formData.answerBy || undefined
      };

      // Add MEDEVAC-specific fields if category is MEDEVAC
      if (formData.category === 'MEDEVAC') {
        updateData.exitPointId = formData.exitPointId || undefined;
        updateData.readyAtHub = formData.readyAtHub || undefined;
        updateData.medevacRemarks = formData.medevacRemarks || undefined;
      }

      await requestsApi.update(id, updateData);

      // Submit for review if requested
      if (submitForReview) {
        await requestsApi.submit(id);
      }

      // Navigate to request detail
      navigate(`/requests/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update request');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  if (error && !request) {
    return (
      <div className="error-page">
        <h2>{t('common.error')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/requests')} className="btn btn-primary">
          <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          {t('requests.backToRequests')}
        </button>
      </div>
    );
  }

  return (
    <div className="edit-request">
      <div className="page-header">
        <h1>{t('requests.editRequest')}</h1>
        <p>{t('requests.requestDescription')}</p>
      </div>

      {error && <div ref={errorRef} className="error">{error}</div>}

      <form className="request-form card" style={{ position: 'relative' }}>
        <LoadingOverlay loading={saving} text="Saving..." />
        <div className="form-section">
          <h3>{t('requests.generalInfo')}</h3>

          <div className="form-group">
            <label htmlFor="title">
              {t('requests.requestTitle')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Brief title of your request"
              required
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              {t('requests.description')} <span className="required">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Detailed description of your assistance request..."
              required
              rows={6}
            />
            <small>Provide as much detail as possible about your needs</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">
                {t('requests.category')} <span className="required">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="PERSONNEL">{t('categories.personnel')}</option>
                <option value="MATERIAL">{t('categories.equipment')}</option>
                <option value="MEDEVAC">{t('categories.medevac')} (Medical Evacuation)</option>
                <option value="OTHER">{t('categories.other')}</option>
                {canCreateNews && <option value="NEWS">{t('categories.news')}</option>}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="urgency">
                {t('requests.urgency')} <span className="required">*</span>
              </label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleInputChange}
                required
              >
                <option value="NORMAL">{t('urgency.routine')}</option>
                <option value="HIGH">{t('urgency.urgent')}</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="answerBy">{t('requests.answerBy')} ({t('common.optional')}) — UTC</label>
            <input
              type="datetime-local"
              id="answerBy"
              name="answerBy"
              value={formData.answerBy}
              onChange={handleInputChange}
            />
            <small className="utc-hint">{t('common.utcStoredHint')}</small>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('requests.additionalInfo')}</h3>

          {formData.category !== 'MEDEVAC' && (
            <div className="form-group">
              <label htmlFor="attachments">
                Attachments
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="attachments"
                  accept=".xlsx,.xls,.docx,.doc,.pdf,.zip"
                  onChange={handleAttachmentChange}
                  multiple
                />
              </div>
              <small>
                Upload supporting documents (Excel, Word, PDF, ZIP). Maximum 5 files, 5 MB each.
              </small>

              {attachments.length > 0 && (
                <div className="attachments-list">
                  {attachments.map((file, index) => (
                    <div key={index} className="file-info">
                      <span className="file-name"><Paperclip size={14} /> {file.name}</span>
                      <span className="file-size">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="btn-remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {formData.category === 'MEDEVAC' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="exitPointId">
                    UCPM TRANSIT HUB <span className="required">*</span>
                  </label>
                  <select
                    id="exitPointId"
                    name="exitPointId"
                    value={formData.exitPointId}
                    onChange={handleInputChange}
                    required={formData.category === 'MEDEVAC'}
                  >
                    <option value="">Select UCPM Hub...</option>
                    {availableStartPoints.map((startPoint) => (
                      <option key={startPoint.id} value={startPoint.id}>
                        {startPoint.name} - {startPoint.types.map((t: string) => t.replace('_', ' ')).join(', ')}
                      </option>
                    ))}
                  </select>
                  {availableStartPoints.length === 0 && (
                    <small className="warning-text">
                      No hubs available in your nation. Please contact EXCON.
                    </small>
                  )}
                  {availableStartPoints.length > 0 && (
                    <small>Select the UCPM TRANSIT HUB where patients will be ready for pickup</small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="readyAtHub">
                    Ready at UCPM TRANSIT HUB (UTC) <span className="required">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="readyAtHub"
                    name="readyAtHub"
                    value={formData.readyAtHub}
                    onChange={handleInputChange}
                    required={formData.category === 'MEDEVAC'}
                  />
                  <small>Enter time in UTC</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="medevacRemarks">MEDEVAC Remarks</label>
                <textarea
                  id="medevacRemarks"
                  name="medevacRemarks"
                  value={formData.medevacRemarks}
                  onChange={handleInputChange}
                  placeholder="Additional remarks or special instructions for the medical evacuation..."
                  rows={4}
                />
                <small>Provide any additional information relevant to the evacuation</small>
              </div>

              {/* Read-only patient overview */}
              {request?.patients && request.patients.length > 0 && (
                <div className="imported-patients-section">
                  <h4>Linked Patients ({request.patients.length})</h4>
                  <p className="help-text">Patients linked to this request. To modify patients, withdraw this request and create a new one.</p>
                  <div className="patients-table-wrapper">
                    <table className="patients-overview-table">
                      <thead>
                        <tr>
                          <th>Patient ID</th>
                          <th>Initials</th>
                          <th>Age</th>
                          <th>Gender</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Diagnosis</th>
                          <th>Transport</th>
                        </tr>
                      </thead>
                      <tbody>
                        {request.patients.map((patient: Patient) => (
                          <tr key={patient.id}>
                            <td><code>{patient.patientId || '-'}</code></td>
                            <td><strong>{patient.patientInitials || '-'}</strong></td>
                            <td>{patient.patientAge || '-'}</td>
                            <td>{patient.patientGender || '-'}</td>
                            <td>
                              <span className={`badge badge-${patient.patientStatus?.toLowerCase() || 'default'}`}>
                                {patient.patientStatus || '-'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge badge-priority-${patient.medicalPriority?.toLowerCase() || 'default'}`}>
                                {patient.medicalPriority || '-'}
                              </span>
                            </td>
                            <td>{patient.medicalPrimaryConditionDiagnosis || '-'}</td>
                            <td>{patient.medicalTransport || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="form-section">
          <h3>{t('requests.reviewSubmit')}</h3>
          <p>
            You can save your changes or submit this request for EU review. Once submitted,
            the request cannot be edited.
          </p>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/requests/${id}`)}
            className="btn btn-secondary"
            disabled={saving}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            className="btn btn-outline"
            disabled={saving}
          >
            {saving ? t('requests.saving') : t('common.save')}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? t('requests.saving') : t('requests.saveAndSubmit')}
          </button>
        </div>
      </form>

    </div>
  );
}

export default EditRequest;
